import type { POSProvider } from "@/types/pos";
import { appConfig } from "@/lib/config";
import {
  bodyIndicatesSubscriptionExpiry,
  markSubscriptionExpired,
} from "@/lib/subscription-bus";
import { reportFeatureDisabled } from "@/lib/feature-bus";
import { safeAppPath } from "@/lib/safe-url";

// ==========================================
// Storage Keys
// ==========================================

// Tier-1 tokens (localStorage - persists across sessions/tabs)
const ACCESS_TOKEN_KEY = "dhaba_access_token";
const REFRESH_TOKEN_KEY = "dhaba_refresh_token";

// Tier-2 staff token (sessionStorage - per-tab session)
const STAFF_TOKEN_KEY = "dhaba_staff_token";
const STAFF_SESSION_KEY = "dhaba_staff_session";

// Brand HQ session (sessionStorage - per-tab; never mixed with staff token)
const BRAND_TOKEN_KEY = "dhaba_brand_token";
const BRAND_SESSION_KEY = "dhaba_brand_session";

// Context selection
const SELECTED_CONTEXT_KEY = "dhaba_selected_context";

// Organization ID (localStorage - survives staff session clears, needed for Tier-1-only endpoints like billing)
const ORG_ID_KEY = "dhaba_organization_id";

// ==========================================
// Session changes (sign-in, outlet / staff pick, switch, sign-out)
// ==========================================

const sessionListeners = new Set<() => void>();

/**
 * Called whenever this tab's sign-in, staff session or selected outlet changes
 * (and when another tab signs in or out). The notification socket follows it.
 */
export function subscribeSessionChange(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

function notifySessionChange(): void {
  sessionListeners.forEach((listener) => listener());
}

if (typeof window !== "undefined") {
  // Another tab signed in or out (the login lives in localStorage).
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key === ACCESS_TOKEN_KEY) notifySessionChange();
  });
}

// ==========================================
// Tier-1 Token Management (User Authentication)
// ==========================================

export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  notifySessionChange();
};

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ORG_ID_KEY);
  // Also clear staff session when user logs out
  clearStaffSession();
  notifySessionChange();
};

// ==========================================
// Tier-2 Token Management (Staff Session)
// ==========================================

export const getStaffToken = (): string | null => {
  return sessionStorage.getItem(STAFF_TOKEN_KEY);
};

export const setStaffToken = (token: string): void => {
  sessionStorage.setItem(STAFF_TOKEN_KEY, token);
  notifySessionChange();
};

export const clearStaffToken = (): void => {
  sessionStorage.removeItem(STAFF_TOKEN_KEY);
  notifySessionChange();
};

// Staff session data (profile info, permissions, etc.)
export interface StaffSessionData {
  profile: {
    /** Staff profile id. The PIN-verify response (StaffProfileResponse) sends it as `id`. */
    id?: string;
    /** @deprecated never sent by the API; kept for sessions stored by older builds. */
    profile_id?: string;
    user_id: string;
    outlet_id: string;
    outlet_name: string;
    role_ids: string[];
    role_names: string[];
    display_name: string;
    employee_code: string | null;
    is_primary: boolean;
    is_active: boolean;
    /** Present when SelectContext stores the full staff profile payload. */
    roles?: { id?: number | string; code?: string; name?: string }[];
    role_codes?: string[];
  };
  permissions: string[];
  session_started_at: string;
  expires_in: number;
  mfa_method?: "totp" | null;
  mfa_verified_at?: string | null;
  assigned_terminal_id?: string | null;
  assigned_terminal_label?: string | null;
  assigned_terminal_provider?: POSProvider | null;
}

export const getStaffSession = (): StaffSessionData | null => {
  const stored = sessionStorage.getItem(STAFF_SESSION_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

export const setStaffSession = (session: StaffSessionData): void => {
  sessionStorage.setItem(STAFF_SESSION_KEY, JSON.stringify(session));
  notifySessionChange();
};

/** Staff keys + selected context only — used when seating a brand so we do not wipe the brand token. */
export const clearStaffTokenOnly = (): void => {
  sessionStorage.removeItem(STAFF_TOKEN_KEY);
  sessionStorage.removeItem(STAFF_SESSION_KEY);
  sessionStorage.removeItem(SELECTED_CONTEXT_KEY);
  notifySessionChange();
};

export const clearStaffSession = (): void => {
  clearStaffTokenOnly();
  clearBrandSession();
};

// ==========================================
// Brand HQ Token Management
// ==========================================

export const getBrandToken = (): string | null => {
  return sessionStorage.getItem(BRAND_TOKEN_KEY);
};

export const setBrandToken = (token: string): void => {
  sessionStorage.setItem(BRAND_TOKEN_KEY, token);
};

export interface BrandSessionData {
  brand: {
    brand_id: string;
    brand_name: string;
    role_code: string;
  };
  expires_in: number;
  session_started_at: string;
}

export const getBrandSession = (): BrandSessionData | null => {
  const stored = sessionStorage.getItem(BRAND_SESSION_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as BrandSessionData;
    } catch {
      return null;
    }
  }
  return null;
};

export const setBrandSession = (session: BrandSessionData): void => {
  sessionStorage.setItem(BRAND_SESSION_KEY, JSON.stringify(session));
};

export const clearBrandSession = (): void => {
  sessionStorage.removeItem(BRAND_TOKEN_KEY);
  sessionStorage.removeItem(BRAND_SESSION_KEY);
};

/** Brand-scoped APIs that take X-Brand-Token. GET /api/v1/auth/brands is Bearer-only.
 *  POST /api/v1/brands/join is a staff call (franchisee org_owner) and must not
 *  attach X-Brand-Token.
 */
function isBrandTokenEndpoint(endpoint: string): boolean {
  const path = endpoint.split("?")[0];
  if (path === "/api/v1/brands/join") return false;
  if (path.startsWith("/api/v1/brands")) return true;
  return (
    path.startsWith("/api/v1/auth/brand") &&
    !path.startsWith("/api/v1/auth/brands")
  );
}

// Check if staff has a specific permission
export const hasPermission = (permission: string): boolean => {
  const session = getStaffSession();
  return session?.permissions?.includes(permission) ?? false;
};

// ==========================================
// Selected Context Management
// ==========================================

interface SelectedContextForAPI {
  kind?: "outlet" | "brand";
  organization?: { id: string; name: string };
  outlet?: {
    id: string;
    name: string;
    code: string;
    is_demo?: boolean;
    diet_type?: "pure_veg" | "vegan" | null;
    /** The organisation's GST registration (false: no GST on bills; GST settings read-only). */
    gst_registered?: boolean;
    /** CRM features for this outlet (false: switched off by Dhaba support; absent = on). */
    crm_enabled?: boolean;
  };
  staffProfile?: { id: string; display_name: string; role_names: string[] };
  brand?: { id: string; name: string; role_code: string } | null;
}

// Persist org ID to localStorage (called at Tier-1 login and context selection)
export const persistOrganizationId = (orgId: string): void => {
  if (orgId && orgId !== "undefined" && orgId !== "null") {
    localStorage.setItem(ORG_ID_KEY, orgId);
  }
};

// Get organization ID: sessionStorage (context selection) → localStorage (Tier-1 login fallback)
export const getOrganizationId = (): string | null => {
  const context = getSelectedContext();
  const id = context?.organization?.id ?? localStorage.getItem(ORG_ID_KEY);
  if (!id || id === "undefined" || id === "null") return null;
  return id;
};

export const getSelectedContext = (): SelectedContextForAPI | null => {
  const stored = sessionStorage.getItem(SELECTED_CONTEXT_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

export const setSelectedContext = (context: SelectedContextForAPI): void => {
  sessionStorage.setItem(SELECTED_CONTEXT_KEY, JSON.stringify(context));
  // Persist org ID in localStorage so it survives staff session clears
  if (context.organization?.id) {
    localStorage.setItem(ORG_ID_KEY, context.organization.id);
  }
  notifySessionChange();
};

export const clearSelectedContext = (): void => {
  sessionStorage.removeItem(SELECTED_CONTEXT_KEY);
  notifySessionChange();
};

// ==========================================
// API Error Class
// ==========================================

/** The human-readable message in an error body: {message} envelope, FastAPI {detail}, or 422 detail[]. */
function serverMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const body = data as { message?: unknown; detail?: unknown; error?: { message?: unknown } };
  if (typeof body.message === "string" && body.message.trim()) return body.message;
  if (typeof body.error?.message === "string" && body.error.message.trim()) return body.error.message;
  if (typeof body.detail === "string" && body.detail.trim()) return body.detail;
  if (Array.isArray(body.detail)) {
    const first = body.detail[0] as { msg?: unknown } | undefined;
    if (typeof first?.msg === "string") return first.msg;
  }
  return undefined;
}

export class ApiError extends Error {
  /** Machine-readable error code from the envelope (e.g. VALIDATION_ERROR), if any. */
  public code?: string;

  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    // Toasts show error.message: prefer what the server said. Over HTTP/2
    // statusText is empty, so "400" alone was all staff ever saw.
    super(
      serverMessage(data) ??
        (statusText ? `${status} ${statusText}` : `Request failed (${status})`),
    );
    this.name = "ApiError";
    const code = (data as { code?: unknown } | null | undefined)?.code;
    if (typeof code === "string") this.code = code;
  }
}

// ==========================================
// Session Expiry Redirect
// ==========================================

let isRedirecting = false;

/** Force-redirect on session expiry (prevents multiple simultaneous redirects). */
function forceRedirect(path: string) {
  // Same-origin paths only; anything else goes to the sign-in page.
  const target = safeAppPath(path, "/");
  if (isRedirecting || window.location.pathname === target) return;
  isRedirecting = true;
  window.location.href = target;
}

// ==========================================
// Token Refresh Logic
// ==========================================

let refreshPromise: Promise<string> | null = null;

const REFRESH_LOCK = "dhaba-token-refresh";

/** The refresh token itself was rejected: the user really is signed out. */
const isRefreshRejected = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 401 || error.status === 403);

/** POST /auth/refresh with `refreshToken`. Only a 401/403 clears the stored tokens. */
const postRefresh = async (refreshToken: string): Promise<string> => {
  let response: Response;
  try {
    response = await fetch(`${appConfig.apiBaseUrl}/api/v1/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // Network failure: the session may be perfectly valid — keep the tokens.
    throw new ApiError(0, "Network error while refreshing the session");
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      // Another tab may have rotated the refresh token while ours was in flight
      // (rotation spends the old one). Use its newer token instead of signing out.
      const current = getRefreshToken();
      const access = getAccessToken();
      if (current && current !== refreshToken && access) return access;
      clearTokens();
    }
    // 5xx and other failures keep the tokens; the next attempt can succeed.
    throw new ApiError(response.status, response.statusText);
  }

  const data = await response.json();
  setTokens(data.access_token, data.refresh_token);
  return data.access_token;
};

const refreshAccessToken = async (): Promise<string> => {
  const startedWith = getRefreshToken();
  if (!startedWith) {
    throw new ApiError(401, "No refresh token available");
  }

  const run = async (): Promise<string> => {
    const latest = getRefreshToken();
    if (!latest) throw new ApiError(401, "No refresh token available");
    // Another tab refreshed while we waited for the lock: reuse its token.
    if (latest !== startedWith) {
      const access = getAccessToken();
      if (access) return access;
    }
    return postRefresh(latest);
  };

  // Tabs share localStorage tokens; serialise refreshes across them so two
  // tabs don't both spend the same (rotating) refresh token.
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  return locks?.request ? locks.request(REFRESH_LOCK, run) : run();
};

/**
 * Refresh for apiFetch callers: maps a rejected refresh to "signed out"
 * (redirect home) and anything else (5xx/network) to a retryable 503
 * without touching the stored tokens.
 */
const refreshForRequest = async (skipForceRedirect: boolean | undefined): Promise<string> => {
  try {
    if (!refreshPromise) refreshPromise = refreshAccessToken();
    return await refreshPromise;
  } catch (error) {
    if (isRefreshRejected(error)) {
      if (!skipForceRedirect) forceRedirect("/");
      throw new ApiError(401, "Session expired");
    }
    throw new ApiError(503, "Couldn't refresh your session (network or server problem). Please try again.");
  } finally {
    refreshPromise = null;
  }
};

/** Seconds until the JWT expires, or null if it can't be read. */
const secondsUntilExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp - Date.now() / 1000 : null;
  } catch {
    return null;
  }
};

/**
 * A usable access token for long-lived connections (WebSocket), refreshed
 * first when it expires within a minute. Returns null when signed out.
 */
export const getFreshAccessToken = async (): Promise<string | null> => {
  const token = getAccessToken();
  if (!token) return null;
  const left = secondsUntilExpiry(token);
  if (left === null || left > 60) return token;
  if (!getRefreshToken()) return null;
  try {
    if (!refreshPromise) refreshPromise = refreshAccessToken();
    return await refreshPromise;
  } catch {
    return null;
  } finally {
    refreshPromise = null;
  }
};

// ==========================================
// Base Fetch with Two-Tier Auth
// ==========================================

export interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  skipStaffToken?: boolean; // Skip Tier-2 token (for endpoints that only need Tier-1)
  skipBrandToken?: boolean; // Never attach X-Brand-Token; send staff token as usual
  skipForceRedirect?: boolean; // Suppress forceRedirect on 401 (caller handles errors)
  /** apiFetch: sees a successful response before its body is read (e.g. for a header). */
  onResponse?: (response: Response) => void;
}

/**
 * The auth headers every API call carries: Tier-1 Bearer, then either
 * X-Brand-Token (brand APIs only) or X-Staff-Token (never both), and X-Org-ID.
 * Shared by apiFetch, apiFetchBlob and apiUpload so they never drift apart.
 */
function authHeaders(
  endpoint: string,
  { skipStaffToken = false, skipBrandToken = false }: { skipStaffToken?: boolean; skipBrandToken?: boolean } = {},
): Record<string, string> {
  const headers: Record<string, string> = {};

  // Tier-1: Bearer token (user authentication)
  const accessToken = getAccessToken();
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  // Brand HQ: X-Brand-Token on brand APIs only — never together with X-Staff-Token.
  // Staff POS URLs never send the brand header.
  const brandToken = getBrandToken();
  if (!skipBrandToken && brandToken && isBrandTokenEndpoint(endpoint)) {
    headers["X-Brand-Token"] = brandToken;
  } else if (!skipStaffToken) {
    const staffToken = getStaffToken();
    if (staffToken) headers["X-Staff-Token"] = staffToken;
  }

  // Organization ID header (required for some endpoints like /outlets/)
  const orgId = getOrganizationId();
  if (orgId) headers["X-Org-ID"] = orgId;

  return headers;
}

export const apiFetch = async <T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> => {
  const {
    skipAuth = false,
    skipStaffToken = false,
    skipBrandToken = false,
    skipForceRedirect = false,
    onResponse,
    ...fetchOptions
  } = options;

  const isFormData = fetchOptions.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...fetchOptions.headers,
  };

  // Add auth headers if not skipping auth
  if (!skipAuth) {
    Object.assign(headers, authHeaders(endpoint, { skipStaffToken, skipBrandToken }));
  }

  let response = await fetch(`${appConfig.apiBaseUrl}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  // Handle 401 — but only when it actually signals token/session expiry.
  //
  // The backend uses 401 for both real session expiry (code=INVALID_TOKEN
  // or no body) AND for domain-level "authentication needed" errors that
  // the caller wants to handle in-band (wrong PIN, missing bootstrap PIN,
  // PIN locked, etc.). Blindly running the refresh+redirect path for the
  // latter would swallow the error body, wipe state, and bounce the user
  // — so we peek at the body first and only escalate when the code says
  // the token itself is the problem.
  if (response.status === 401 && !skipAuth) {
    const peeked = await response.clone().json().catch(() => null);
    const code = (peeked as { code?: string } | null)?.code;
    const isSessionExpiry = !code || code === "INVALID_TOKEN";

    if (isSessionExpiry) {
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        // One refresh at a time (per tab and across tabs); 5xx/network keep the session.
        const newToken = await refreshForRequest(skipForceRedirect);

        // Retry request with new token
        (headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
        response = await fetch(`${appConfig.apiBaseUrl}${endpoint}`, {
          ...fetchOptions,
          headers,
        });

        // Still 401 after refresh → staff session is invalid
        if (response.status === 401) {
          clearStaffSession();
          if (!skipForceRedirect) forceRedirect("/select-context");
          throw new ApiError(401, "Staff session expired");
        }
      } else {
        // No refresh token → user auth is gone
        clearTokens();
        if (!skipForceRedirect) forceRedirect("/");
        throw new ApiError(401, "Session expired");
      }
    }
    // Domain-level 401 (e.g. BOOTSTRAP_PIN_REQUIRED, PIN_INVALID,
    // PIN_LOCKED) — fall through to the !response.ok branch below so the
    // body is preserved on the thrown ApiError.
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    // Detect subscription-expired signals on every response so the gate
    // flips even on endpoints we haven't explicitly subscription-guarded:
    //   - 402 is the canonical "payment required" status.
    //   - 403 counts only if the body carries the SUBSCRIPTION_EXPIRED code,
    //     so ordinary permission-denied 403s don't accidentally trigger.
    if (
      response.status === 402 ||
      (response.status === 403 && bodyIndicatesSubscriptionExpiry(data))
    ) {
      markSubscriptionExpired();
    }
    // 403 FEATURE_DISABLED: a feature (e.g. CRM) is switched off for the outlet.
    reportFeatureDisabled(response.status, data);
    throw new ApiError(response.status, response.statusText, data);
  }

  onResponse?.(response);

  // 204 No Content has no body to parse
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
};

// ==========================================
// Blob fetch (for binary endpoints like image/pdf renders)
// ==========================================

// Fetch an endpoint that returns binary content (e.g. PNG, PDF) and yield a
// Blob. Mirrors apiFetch's auth + 401-refresh behaviour but parses the body
// as a Blob instead of JSON. Used for QR image rendering where we need to
// inject Authorization/X-Staff-Token/X-Org-ID headers that <img src=…> can't.
export const apiFetchBlob = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<Blob> => {
  const { skipAuth = false, skipStaffToken = false, skipBrandToken = false, skipForceRedirect = false, ...fetchOptions } = options;

  const headers: HeadersInit = { ...fetchOptions.headers };

  if (!skipAuth) {
    Object.assign(headers, authHeaders(endpoint, { skipStaffToken, skipBrandToken }));
  }

  let response = await fetch(`${appConfig.apiBaseUrl}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (response.status === 401 && !skipAuth) {
    const peeked = await response.clone().json().catch(() => null);
    const code = (peeked as { code?: string } | null)?.code;
    const isSessionExpiry = !code || code === "INVALID_TOKEN";

    if (isSessionExpiry) {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const newToken = await refreshForRequest(skipForceRedirect);
        (headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
        response = await fetch(`${appConfig.apiBaseUrl}${endpoint}`, {
          ...fetchOptions,
          headers,
        });
        if (response.status === 401) {
          clearStaffSession();
          if (!skipForceRedirect) forceRedirect("/select-context");
          throw new ApiError(401, "Staff session expired");
        }
      } else {
        clearTokens();
        if (!skipForceRedirect) forceRedirect("/");
        throw new ApiError(401, "Session expired");
      }
    }
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    if (
      response.status === 402 ||
      (response.status === 403 && bodyIndicatesSubscriptionExpiry(data))
    ) {
      markSubscriptionExpired();
    }
    // 403 FEATURE_DISABLED: a feature (e.g. CRM) is switched off for the outlet.
    reportFeatureDisabled(response.status, data);
    throw new ApiError(response.status, response.statusText, data);
  }

  return response.blob();
};

// Trigger a browser download for an authenticated binary endpoint.
// Fetches via apiFetchBlob, materialises an object URL, clicks a synthetic
// <a download>, and revokes the URL after the click.
export const downloadAuthenticatedFile = async (
  endpoint: string,
  filename: string
): Promise<void> => {
  const blob = await apiFetchBlob(endpoint);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ==========================================
// Multipart upload with progress
// ==========================================

export interface UploadOptions
  extends Pick<FetchOptions, "skipStaffToken" | "skipBrandToken" | "skipForceRedirect" | "signal"> {
  /** Upload progress as a fraction 0..1 (request body sent, not server processing). */
  onProgress?: (fraction: number) => void;
}

interface RawResponse {
  status: number;
  statusText: string;
  data: unknown;
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** One XHR POST. Resolves with any HTTP status; rejects only on network failure or abort. */
function xhrPost(
  url: string,
  headers: Record<string, string>,
  body: FormData,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal | null,
): Promise<RawResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    // No Content-Type: the browser sets multipart/form-data with its boundary.
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
      };
    }
    xhr.onload = () =>
      resolve({ status: xhr.status, statusText: xhr.statusText, data: parseJson(xhr.responseText) });
    xhr.onerror = () =>
      reject(new ApiError(0, "Network error during upload. Check your connection and try again."));
    xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  });
}

/**
 * POST multipart/form-data with upload progress. Uses XMLHttpRequest (fetch
 * cannot report request-body progress) and falls back to apiFetch when XHR is
 * unavailable. Same auth headers, 401 refresh-and-retry and error mapping as
 * apiFetch. Never sets Content-Type, so the multipart boundary stays intact.
 */
export const apiUpload = async <T>(
  endpoint: string,
  body: FormData,
  options: UploadOptions = {},
): Promise<T> => {
  const { onProgress, skipStaffToken, skipBrandToken, skipForceRedirect, signal } = options;

  if (typeof XMLHttpRequest === "undefined") {
    const result = await apiFetch<T>(endpoint, {
      method: "POST",
      body,
      signal,
      skipStaffToken,
      skipBrandToken,
      skipForceRedirect,
    });
    onProgress?.(1);
    return result;
  }

  const url = `${appConfig.apiBaseUrl}${endpoint}`;
  const headers = authHeaders(endpoint, { skipStaffToken, skipBrandToken });
  let response = await xhrPost(url, headers, body, onProgress, signal);

  // Same rule as apiFetch: only a token-level 401 triggers refresh + retry.
  if (response.status === 401) {
    const code = (response.data as { code?: string } | null)?.code;
    if (!code || code === "INVALID_TOKEN") {
      if (getRefreshToken()) {
        const newToken = await refreshForRequest(skipForceRedirect);
        headers["Authorization"] = `Bearer ${newToken}`;
        response = await xhrPost(url, headers, body, onProgress, signal);
        if (response.status === 401) {
          clearStaffSession();
          if (!skipForceRedirect) forceRedirect("/select-context");
          throw new ApiError(401, "Staff session expired");
        }
      } else {
        clearTokens();
        if (!skipForceRedirect) forceRedirect("/");
        throw new ApiError(401, "Session expired");
      }
    }
  }

  if (response.status < 200 || response.status >= 300) {
    if (
      response.status === 402 ||
      (response.status === 403 && bodyIndicatesSubscriptionExpiry(response.data))
    ) {
      markSubscriptionExpired();
    }
    reportFeatureDisabled(response.status, response.data);
    throw new ApiError(response.status, response.statusText, response.data);
  }

  onProgress?.(1);
  return response.data as T;
};

// ==========================================
// Convenience Methods
// ==========================================

export const api = {
  get: <T>(endpoint: string, options?: FetchOptions) =>
    apiFetch<T>(endpoint, { ...options, method: "GET" }),

  post: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: FetchOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, options?: FetchOptions) =>
    apiFetch<T>(endpoint, { ...options, method: "DELETE" }),

  postForm: <T>(endpoint: string, body: FormData, options?: FetchOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: "POST",
      body,
    }),
};
